import { useQuery } from "@tanstack/react-query";
import { Visit } from "../types/visit";

interface UseVisitHistoryOptions {
  search?: string;
  dateRange?: [Date | null, Date | null];
  status?: string;
  patientId?: string;
}

async function fetchVisitHistory(options: UseVisitHistoryOptions): Promise<Visit[]> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));

  // Mock data with a variety of visit types and statuses
  const visits: Visit[] = [
    {
      id: "V1001",
      patientId: "P1001",
      clinicId: "C1001",
      doctorId: "1",
      startTime: "2024-03-15T09:00:00",
      endTime: "2024-03-15T09:30:00",
      duration: 30,
      type: "regular",
      status: "completed",
      reason: "Regular Check-up",
      title: "Regular Check-up",
      notes: "Patient reported mild fever and cough",
      diagnosis: "Upper Respiratory Infection",
      symptoms: ["fever", "cough", "fatigue"],
      prescriptions: [{
        medication: "Amoxicillin",
        dosage: "500mg",
        frequency: "3x daily",
        duration: "7 days",
        notes: "Take with food"
      }],
      vitals: {
        temperature: 38.2,
        bloodPressure: "120/80",
        heartRate: 82,
        respiratoryRate: 16
      },
      createdAt: "2024-03-15T09:00:00",
      updatedAt: "2024-03-15T09:30:00"
    },
    {
      id: "V1002",
      patientId: "P1001",
      clinicId: "C1001",
      doctorId: "1",
      startTime: "2024-02-20T14:00:00",
      endTime: "2024-02-20T14:45:00",
      duration: 45,
      type: "regular",
      status: "completed",
      reason: "Follow-up",
      title: "Follow-up Visit",
      notes: "Follow-up for previous URI, symptoms improved",
      diagnosis: "Resolving URI",
      symptoms: ["mild cough"],
      vitals: {
        temperature: 37.0,
        bloodPressure: "118/78",
        heartRate: 75,
        respiratoryRate: 14
      },
      createdAt: "2024-02-20T14:00:00",
      updatedAt: "2024-02-20T14:45:00"
    },
    {
      id: "V1003",
      patientId: "P1001",
      clinicId: "C1001",
      doctorId: "2",
      startTime: "2024-01-10T11:30:00",
      endTime: "2024-01-10T12:00:00",
      duration: 30,
      type: "regular",
      status: "completed",
      reason: "Regular Check-up",
      title: "Annual Physical",
      notes: "Annual health check-up, all vitals normal",
      diagnosis: "Healthy, no concerns",
      vitals: {
        temperature: 36.8,
        bloodPressure: "122/82",
        heartRate: 70,
        respiratoryRate: 14
      },
      createdAt: "2024-01-10T11:30:00",
      updatedAt: "2024-01-10T12:00:00"
    },
    {
      id: "V1004",
      patientId: "P1001",
      clinicId: "C1001",
      doctorId: "1",
      startTime: "2024-03-25T10:00:00",
      endTime: "2024-03-25T10:30:00",
      duration: 30,
      type: "regular",
      status: "scheduled",
      reason: "Vaccination",
      title: "Vaccination Appointment",
      notes: "Scheduled for annual flu shot",
      createdAt: "2024-03-15T10:00:00",
      updatedAt: "2024-03-15T10:00:00"
    },
    {
      id: "V1005",
      patientId: "P1001",
      clinicId: "C1002",
      doctorId: "2",
      startTime: "2023-12-05T15:00:00",
      endTime: "2023-12-05T15:45:00",
      duration: 45,
      type: "outreach",
      status: "completed",
      reason: "Regular Check-up",
      title: "Community Health Screening",
      notes: "Participated in community health screening program",
      diagnosis: "Mild hypertension detected",
      vitals: {
        temperature: 37.0,
        bloodPressure: "138/88",
        heartRate: 78,
        respiratoryRate: 15
      },
      createdAt: "2023-12-05T15:00:00",
      updatedAt: "2023-12-05T15:45:00"
    },
    {
      id: "V1006",
      patientId: "P1002", // Different patient
      clinicId: "C1001",
      doctorId: "1",
      startTime: "2024-03-16T14:00:00",
      endTime: "2024-03-16T14:45:00",
      duration: 45,
      type: "regular",
      status: "completed",
      reason: "Regular Check-up",
      title: "Regular Check-up",
      notes: "Routine check-up",
      diagnosis: "Healthy",
      vitals: {
        temperature: 36.7,
        bloodPressure: "120/80",
        heartRate: 72
      },
      createdAt: "2024-03-16T14:00:00",
      updatedAt: "2024-03-16T14:45:00"
    }
  ];

  // Filter by patientId if provided
  if (options.patientId) {
    return visits.filter(visit => visit.patientId === options.patientId);
  }

  return visits;
}

export function useVisitHistory(options: UseVisitHistoryOptions = {}) {
  return useQuery({
    queryKey: ["visit-history", options],
    queryFn: () => fetchVisitHistory(options),
  });
}
